"use client"

import React from "react"
import { useRouter } from "next/navigation"
import { updatePunchoutConfigurationAction } from "@/actions/ociSettingsActions"
import { CircleAlertIcon, Info, XCircleIcon } from "lucide-react"

import {
  PunchoutConfiguration,
  PunchoutConfigurationItem,
  PunchoutConfigurationItemMapping,
} from "@/lib/storefront/account/oci-service"
import { Button } from "@/components/ui/components"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/components/tooltip"
import { RichtextStoryblok } from "@/components/storyblok/component-types"
import UbRichText from "@/components/storyblok/UbRichText"

import GlobalMessageBanner from "./GlobalMessageBanner"

interface AccountOciPunchoutConfigClientProps {
  punchoutConfig?: PunchoutConfiguration | undefined
  labelTitle?: string
  labelSubtitle?: string
  labelLoadError?: string
  labelUpdateSuccess?: string
  labelUpdateError?: string
  labelField?: string
  labelTransform?: string
  labelTransformErrorPlaceholderInvalid?: string
  tooltipTransformTitle?: string
  tooltipTransformDescription?: RichtextStoryblok
  labelMappings?: string
  tooltipMappingTitle?: string
  tooltipMappingDescription?: RichtextStoryblok
  labelNoMappings?: string
  labelAddMapping?: string
  labelMappingErrorParameter?: string
  labelMappingErrorFrom?: string
  labelMappingErrorTo?: string
  labelFormatter?: string
  labelFormatterNone?: string
  labelFormatterUpperCase?: string
  labelFormatterLowerCase?: string
  labelFormatterTrim?: string
  labelFormatterUnitsMapping?: string
  labelFormatterRemoveHTMLTags?: string
  labelBtnSubmit?: string
  labelBtnCancel?: string
}

type MappingError = { from?: string; to?: string }
type ItemError = { parameter?: string; mappings?: MappingError[] }

export default function AccountOciPunchoutConfigClient(props: AccountOciPunchoutConfigClientProps) {
  const router = useRouter()
  const [punchoutConfig, setPunchoutConfig] = React.useState<PunchoutConfiguration | undefined>(props.punchoutConfig)
  const [errors, setErrors] = React.useState<Record<number, ItemError>>({})
  const [placeholderErrors, setPlaceholderErrors] = React.useState<Record<number, string>>({})
  const [globalMessage, setGlobalMessage] = React.useState<{ type: "success" | "error"; text: string } | null>(null)
  const globalMessageRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (globalMessage) {
      setGlobalMessage(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.punchoutConfig])

  const items: PunchoutConfigurationItem[] = (punchoutConfig?.items ?? []) as PunchoutConfigurationItem[]

  function showUpdateError(scroll = true) {
    setGlobalMessage({ type: "error", text: props.labelUpdateError ?? "Fehler beim Speichern der Konfiguration" })
    if (scroll) {
      setTimeout(() => {
        if (globalMessageRef.current) {
          globalMessageRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 50)
    }
  }

  function updateItem(index: number, patch: Partial<PunchoutConfigurationItem>) {
    setPunchoutConfig((prev) => {
      const existing = prev ?? { items: [] }
      const items = (existing.items ?? []).map((it, i) => (i === index ? { ...(it ?? {}), ...(patch as any) } : it))
      return { ...existing, items }
    })
    setErrors((prev) => {
      if (!prev[index]) return prev
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  function updateMapping(itemIndex: number, mappingIndex: number, patch: Partial<PunchoutConfigurationItemMapping>) {
    setPunchoutConfig((prev) => {
      const existing = prev ?? { items: [] }
      const items = (existing.items ?? []).map((it, i) => {
        if (i !== itemIndex) return it
        const mappings = (it?.mappings ?? []).map((m, mi) => (mi === mappingIndex ? { ...(m ?? {}), ...patch } : m))
        return { ...(it ?? {}), mappings }
      })
      return { ...existing, items }
    })
    setErrors((prev) => {
      const itemErr = prev[itemIndex]
      if (!itemErr || !itemErr.mappings) return prev
      const next = { ...prev }
      const mappings = [...(itemErr.mappings ?? [])]
      mappings[mappingIndex] = {}
      next[itemIndex] = { ...itemErr, mappings }
      return next
    })
  }

  function addMapping(itemIndex: number) {
    setPunchoutConfig((prev) => {
      const existing = prev ?? { items: [] }
      const items = (existing.items ?? []).map((it, i) => {
        if (i !== itemIndex) return it
        const mappings = [...(it?.mappings ?? []), { mapFromValue: "", mapToValue: "" }]
        return { ...(it ?? {}), mappings }
      })
      return { ...existing, items }
    })
  }

  function removeMapping(itemIndex: number, mappingIndex: number) {
    setPunchoutConfig((prev) => {
      const existing = prev ?? { items: [] }
      const items = (existing.items ?? []).map((it, i) => {
        if (i !== itemIndex) return it
        const mappings = (it?.mappings ?? []).filter((_, mi) => mi !== mappingIndex)
        return { ...(it ?? {}), mappings }
      })
      return { ...existing, items }
    })

    const currMappings = (punchoutConfig?.items?.[itemIndex]?.mappings ?? []) as PunchoutConfigurationItemMapping[]
    const remainingMappingCount = currMappings.filter((_, mi) => mi !== mappingIndex).length

    setErrors((prev) => {
      const itemErr = prev[itemIndex]
      if (!itemErr) return prev
      const next = { ...prev }
      const mappings = (itemErr.mappings ?? []).filter((_, mi) => mi !== mappingIndex)
      const newItemErr: ItemError = {}
      if (mappings.length > 0) {
        newItemErr.mappings = mappings
      }
      if (remainingMappingCount > 0 && itemErr.parameter) {
        newItemErr.parameter = itemErr.parameter
      }
      if (Object.keys(newItemErr).length === 0) {
        delete next[itemIndex]
      } else {
        next[itemIndex] = newItemErr
      }
      return next
    })
  }

  function validate(): boolean {
    const newErrors: Record<number, ItemError> = {}
    let valid = true

    items.forEach((item, idx) => {
      const itemErr: ItemError = {}
      const mappings = item?.mappings ?? []

      if (mappings.length > 0) {
        const mappingErrs: MappingError[] = mappings.map((m) => {
          const me: MappingError = {}
          if (!m?.mapFromValue || String(m.mapFromValue).trim() === "") {
            me.from = props.labelMappingErrorFrom ?? 'Der "Mapping von"-Wert fehlt.'
            valid = false
          }
          if (!m?.mapToValue || String(m.mapToValue).trim() === "") {
            me.to = props.labelMappingErrorTo ?? 'Der "Mapping nach"-Wert fehlt.'
            valid = false
          }
          return me
        })
        if (mappingErrs.some((me) => me.from || me.to)) {
          itemErr.mappings = mappingErrs
        }

        if (!item?.transform || String(item.transform).trim() === "") {
          itemErr.parameter = props.labelMappingErrorParameter ?? 'Der "Parameter"-Wert fehlt.'
          valid = false
        }
      }

      if (itemErr.parameter || itemErr.mappings) {
        newErrors[idx] = itemErr
      }
    })

    setErrors(newErrors)

    if (!valid) {
      try {
        const keys = Object.keys(newErrors)
        if (keys.length > 0) {
          const firstIdx = Math.min(...keys.map((k) => parseInt(k, 10)))
          const el = document.getElementById(`oci-row-${firstIdx}`)
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" })
            const focusEl = el.querySelector("input, select, textarea, button") as HTMLElement | null
            if (focusEl && typeof focusEl.focus === "function") {
              focusEl.focus()
            }
          }
        }
      } catch (e) {
        console.error("Failed to scroll to invalid field", e)
      }
    }

    return valid
  }

  return (
    <div className={"flex flex-col gap-4"}>
      <div className={"flex flex-row justify-between gap-2"}>
        <div>
          <span className={"text-2xl font-semibold"}>{props.labelTitle}</span>
          <p>{props.labelSubtitle}</p>
        </div>
      </div>
      <div className={"w-full"}>
        {punchoutConfig ? (
          <form>
            <GlobalMessageBanner
              message={globalMessage}
              refEl={globalMessageRef}
              onCloseAction={() => setGlobalMessage(null)}
            />
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="border-b bg-gray-50 p-3 text-left font-semibold">
                      {props.labelField ?? "Attributname"}
                    </th>
                    <th className="border-b bg-gray-50 p-3 text-left font-semibold">
                      <div className="flex items-center gap-2">
                        <span>{props.labelTransform ?? "Transformieren"}</span>
                        {(props.tooltipTransformTitle || props.tooltipTransformDescription) && (
                          <TooltipProvider delayDuration={50} skipDelayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="w-fit cursor-pointer">
                                  <Info className="size-4 text-gray-400" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                {props.tooltipTransformTitle && (
                                  <div className="mb-1 font-semibold">{props.tooltipTransformTitle}</div>
                                )}
                                {props.tooltipTransformDescription && (
                                  <UbRichText
                                    blok={
                                      {
                                        component: "ub_rich_text",
                                        _uid: "tooltip_transform_description",
                                        content: props.tooltipTransformDescription,
                                      } as any
                                    }
                                  />
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </th>
                    <th className="border-b bg-gray-50 p-3 text-left font-semibold">
                      <div className="flex items-center gap-2">
                        <span>{props.labelMappings ?? "Mapping"}</span>
                        {(props.tooltipMappingTitle || props.tooltipMappingDescription) && (
                          <TooltipProvider delayDuration={50} skipDelayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="w-fit cursor-pointer">
                                  <Info className="size-4 text-gray-400" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                {props.tooltipMappingTitle && (
                                  <div className="mb-1 font-semibold">{props.tooltipMappingTitle}</div>
                                )}
                                {props.tooltipMappingDescription && (
                                  <UbRichText
                                    blok={
                                      {
                                        component: "ub_rich_text",
                                        _uid: "tooltip_mapping_description",
                                        content: props.tooltipMappingDescription,
                                      } as any
                                    }
                                  />
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </th>
                    <th className="border-b bg-gray-50 p-3 text-left font-semibold">
                      {props.labelFormatter ?? "Format"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const formatterValue = item?.formatter ?? ""
                    const formatterOptions = [
                      { value: "", label: props.labelFormatterNone },
                      { value: "UpperCase", label: props.labelFormatterUpperCase },
                      { value: "LowerCase", label: props.labelFormatterLowerCase },
                      { value: "Trim", label: props.labelFormatterTrim },
                      { value: "UnitsMapping", label: props.labelFormatterUnitsMapping },
                      { value: "RemoveHTMLTags", label: props.labelFormatterRemoveHTMLTags },
                    ]
                    const itemError = errors[idx]
                    const placeholderError = placeholderErrors[idx]
                    return (
                      <React.Fragment key={idx}>
                        {placeholderError && (
                          <tr className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td colSpan={4} className="border p-0 align-top">
                              <div className="m-3">
                                <div
                                  className={
                                    "rounded-sm border-t-4 border-red-600 bg-red-50 px-3 py-2 text-red-900 shadow-xs"
                                  }
                                  role="status"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <CircleAlertIcon className={"size-5 text-red-600"} />
                                      <div className="text-sm font-semibold">
                                        {props.labelTransformErrorPlaceholderInvalid ?? placeholderError}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      aria-label="close"
                                      className="text-muted-foreground"
                                      onClick={() =>
                                        setPlaceholderErrors((prev) => {
                                          const next = { ...prev }
                                          delete next[idx]
                                          return next
                                        })
                                      }
                                    >
                                      <XCircleIcon className={"size-5"} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        <tr id={`oci-row-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          <td className="w-1/4 border p-3 align-top">
                            <div className="text-xs font-semibold text-gray-700 uppercase">{item?.field ?? "-"}</div>
                          </td>

                          <td className="w-1/4 border p-3 align-top">
                            <input
                              value={item?.transform ?? ""}
                              onChange={(e) => updateItem(idx, { transform: e.target.value })}
                              className={
                                `w-full rounded-lg bg-white px-3 py-2 text-sm outline-hidden focus:border-gray-200 ` +
                                (itemError?.parameter ? "border-red-600" : "border-gray-300")
                              }
                            />
                            {itemError?.parameter && (
                              <p className="mt-1 text-xs text-red-600" role="alert">
                                {itemError.parameter}
                              </p>
                            )}
                          </td>

                          <td className="w-1/4 border p-3 align-top">
                            <div className="flex flex-col gap-2">
                              {(item?.mappings ?? []).length === 0 && (
                                <div className="text-xs text-gray-400">
                                  {props.labelNoMappings ?? "Keine Mappings vorhanden"}
                                </div>
                              )}
                              {(item?.mappings ?? []).map((m, mi) => {
                                const mappingErr = itemError?.mappings ? itemError.mappings[mi] : undefined
                                return (
                                  <div key={mi} className="flex flex-col gap-1">
                                    <div className="flex items-center gap-2">
                                      <input
                                        value={m?.mapFromValue ?? ""}
                                        onChange={(e) => updateMapping(idx, mi, { mapFromValue: e.target.value })}
                                        className={
                                          "w-1/2 rounded-lg bg-white px-3 py-2 text-sm outline-hidden " +
                                          (mappingErr?.from ? "border-red-600" : "border-gray-300")
                                        }
                                      />
                                      <div className="text-gray-500">→</div>
                                      <input
                                        value={m?.mapToValue ?? ""}
                                        onChange={(e) => updateMapping(idx, mi, { mapToValue: e.target.value })}
                                        className={
                                          "w-1/2 rounded-lg bg-white px-3 py-2 text-sm outline-hidden " +
                                          (mappingErr?.to ? "border-red-600" : "border-gray-300")
                                        }
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removeMapping(idx, mi)}
                                        className="ml-2 text-sm text-red-600"
                                      >
                                        Entfernen
                                      </button>
                                    </div>
                                    {(mappingErr?.from || mappingErr?.to) && (
                                      <div>
                                        {mappingErr?.from && (
                                          <p className="mt-1 text-xs text-red-600">{mappingErr.from}</p>
                                        )}
                                        {mappingErr?.to && <p className="mt-1 text-xs text-red-600">{mappingErr.to}</p>}
                                      </div>
                                    )}
                                  </div>
                                )
                              })}

                              <button
                                type="button"
                                onClick={() => addMapping(idx)}
                                className="text-xs text-gray-600 hover:underline"
                              >
                                {props.labelAddMapping ?? "Zeile hinzufügen"}
                              </button>
                            </div>
                          </td>

                          <td className="w-1/5 border p-3 align-top">
                            <select
                              value={formatterValue}
                              onChange={(e) => updateItem(idx, { formatter: e.target.value })}
                              className={"w-full rounded-lg border-gray-300 bg-white px-3 py-2 text-sm outline-hidden"}
                            >
                              {formatterOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                              {formatterValue !== "" && !formatterOptions.some((o) => o.value === formatterValue) && (
                                <option value={formatterValue}>{formatterValue}</option>
                              )}
                            </select>
                          </td>
                        </tr>
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex gap-3">
              <Button
                type="button"
                variant="destructive"
                size="defaultNoGrow"
                onClick={async () => {
                  if (!validate()) return
                  try {
                    const res = await updatePunchoutConfigurationAction(punchoutConfig as PunchoutConfiguration)
                    if (res?.message == "") {
                      setGlobalMessage({
                        type: "success",
                        text: props.labelUpdateSuccess ?? "OCI-Konfiguration erfolgreich gespeichert.",
                      })
                      setPlaceholderErrors({})
                      setTimeout(() => {
                        if (globalMessageRef.current) {
                          globalMessageRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
                        }
                      }, 50)
                      setTimeout(() => router.refresh(), 1200)
                    } else if (res?.message === "INVALID_PLACEHOLDER") {
                      const fieldName = res.field
                      if (fieldName) {
                        const idx = items.findIndex((it) => it?.field === fieldName)
                        if (idx >= 0) {
                          setPlaceholderErrors({
                            [idx]:
                              props.labelTransformErrorPlaceholderInvalid ??
                              `Der Platzhalter für ${fieldName} ist ungültig.`,
                          })
                          setTimeout(() => {
                            try {
                              const el = document.getElementById(`oci-row-${idx}`)
                              if (el) {
                                el.scrollIntoView({ behavior: "smooth", block: "center" })
                                const focusEl = el.querySelector(
                                  "input, select, textarea, button"
                                ) as HTMLElement | null
                                if (focusEl && typeof focusEl.focus === "function") {
                                  focusEl.focus()
                                }
                              }
                            } catch (e) {
                              console.error("Failed to scroll to placeholder error row", e)
                            }
                          }, 50)
                        } else {
                          showUpdateError()
                        }
                      } else {
                        showUpdateError()
                      }
                    } else {
                      showUpdateError()
                    }
                  } catch (e) {
                    console.error("Failed to update punchout configuration", e)
                    showUpdateError()
                  }
                }}
              >
                {props.labelBtnSubmit ?? "Änderungen speichern"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="defaultNoGrow"
                onClick={() => router.push("/account/oci/punchout")}
              >
                {props.labelBtnCancel ?? "Abbrechen"}
              </Button>
            </div>
          </form>
        ) : (
          <GlobalMessageBanner
            message={{ type: "error", text: props.labelLoadError ?? "Fehler beim Laden der Konfiguration" }}
            showClose={false}
          />
        )}
      </div>
    </div>
  )
}
